// ============================================================================
// COURT PLACE FORM MODAL — Add / Edit Court Place (MSTR-COURTPL-001)
// ============================================================================
// State is now FK to bms.tbl_state_province (was free text). Uses seedOptions
// pattern for editing when a previously-selected state is now inactive.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';
import { SyutSelect } from 'components/ui';
import notificationService from 'services/notificationService';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import { courtPlaceService } from 'api/services/courtPlaceService';
import type { CourtPlace } from 'api/types/courtPlace.types';

type PlaceStatus = 'ACTIVE' | 'INACTIVE';

interface CourtPlaceFormData {
  placeCode: string;
  placeName: string;
  stateProvinceId: string;
  pincode: string;
  statusCode: PlaceStatus;
}

const INITIAL_FORM_DATA: CourtPlaceFormData = {
  placeCode: '',
  placeName: '',
  stateProvinceId: '',
  pincode: '',
  statusCode: 'ACTIVE',
};

const STATUS_OPTIONS = [
  { value: 'ACTIVE', label: 'Active' },
  { value: 'INACTIVE', label: 'Inactive' },
];

export interface StateProvinceOption {
  value: string;
  label: string;
}

export interface CourtPlaceFormModalProps {
  isOpen: boolean;
  mode: 'create' | 'edit' | 'copy' | 'view';
  record?: CourtPlace | null;
  /** State/province options from bms.tbl_state_province (filtered to active India states). */
  stateProvinceOptions: StateProvinceOption[];
  /** Seeded option when editing a record whose state is now inactive/deleted. */
  seedStateProvinceOptions?: StateProvinceOption[];
  onSaved: () => void;
  onClose: () => void;
  tabId?: string;
  moduleName?: string;
  onRestoreModal?: (mode: 'create' | 'edit' | 'copy' | 'view', instanceId?: string, recordCode?: string) => void;
  createInstanceId?: string;
  minimizeRef?: React.RefObject<(() => void) | null>;
}

const CourtPlaceFormModal: React.FC<CourtPlaceFormModalProps> = ({
  isOpen, mode, record, stateProvinceOptions, seedStateProvinceOptions,
  onSaved, onClose,
  tabId, moduleName, onRestoreModal, createInstanceId, minimizeRef,
}) => {
  const { minimize } = useModalMinimize();

  const combinedStateOptions = useMemo(() => {
    const blank = [{ value: '', label: '— No state —' }];
    if (!seedStateProvinceOptions || seedStateProvinceOptions.length === 0) {
      return [...blank, ...stateProvinceOptions];
    }
    const seedIds = new Set(seedStateProvinceOptions.map(s => s.value));
    return [
      ...blank,
      ...stateProvinceOptions.filter(o => !seedIds.has(o.value)),
      ...seedStateProvinceOptions,
    ];
  }, [stateProvinceOptions, seedStateProvinceOptions]);

  const [formData, setFormData] = useState<CourtPlaceFormData>(INITIAL_FORM_DATA);
  const formDataRef = useRef(formData); formDataRef.current = formData;
  const recordRef = useRef(record); recordRef.current = record;
  const initialSnapshotRef = useRef('');
  const modeRef = useRef(mode); modeRef.current = mode;
  const createInstanceIdRef = useRef(createInstanceId); createInstanceIdRef.current = createInstanceId;
  const isRestoreRef = useRef(false);

  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const getFieldError = useCallback((f: string) => fieldErrors[f], [fieldErrors]);
  const clearFieldError = useCallback((f: string) => {
    setFieldErrors(p => { if (!p[f]) return p; const n = { ...p }; delete n[f]; return n; });
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isOpen) return;
    if (isRestoreRef.current) { isRestoreRef.current = false; setFieldErrors({}); return; }
    const rec = recordRef.current;
    const data: CourtPlaceFormData = rec
      ? {
          placeCode: rec.placeCode,
          placeName: rec.placeName,
          stateProvinceId: rec.stateProvinceId ?? '',
          pincode: rec.pincode ?? '',
          statusCode: (rec.statusCode === 'INACTIVE' ? 'INACTIVE' : 'ACTIVE') as PlaceStatus,
        }
      : { ...INITIAL_FORM_DATA };
    setFormData(data);
    initialSnapshotRef.current = JSON.stringify(data);
    setFieldErrors({});
  }, [isOpen]);

  const isDirty = useMemo(() => {
    if (!initialSnapshotRef.current) return false;
    return JSON.stringify(formData) !== initialSnapshotRef.current;
  }, [formData]);

  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const handleClose = useCallback(() => {
    if (isDirty) setDiscardConfirmOpen(true); else onClose();
  }, [isDirty, onClose]);

  const handleMinimize = useCallback(() => {
    if (!tabId || !moduleName) return;
    const currentFormData = formDataRef.current;
    const currentMode = modeRef.current;
    const currentCreateInstanceId = createInstanceIdRef.current;
    const currentRecord = recordRef.current;

    let modalId: string;
    if (currentMode === 'create') {
      modalId = `${tabId}-form-modal-create-${currentCreateInstanceId || 'default'}`;
    } else {
      modalId = `${tabId}-form-modal-edit-${currentRecord?.placeCode || currentFormData.placeCode || 'new'}`;
    }

    let title: string;
    if (currentMode === 'create') {
      const code = currentFormData.placeCode?.trim();
      const name = currentFormData.placeName?.trim();
      const idx = currentCreateInstanceId || '1';
      title = code ? `Add - ${code} (${idx})` : name ? `Add - ${name} (${idx})` : `Add New (${idx})`;
    } else {
      title = `Edit - ${currentFormData.placeCode || currentRecord?.placeCode || 'Court Place'}`;
    }

    const savedFormData = { ...currentFormData };
    const savedSnapshot = initialSnapshotRef.current;
    const currentIsDirty = savedSnapshot ? JSON.stringify(currentFormData) !== savedSnapshot : false;

    minimize({
      id: modalId, title, moduleName, tabId, isDirty: currentIsDirty,
      onRestore: () => {
        isRestoreRef.current = true;
        setFormData(savedFormData);
        initialSnapshotRef.current = savedSnapshot;
        if (onRestoreModal) {
          onRestoreModal(
            currentMode,
            currentMode === 'create' ? currentCreateInstanceId : undefined,
            currentMode !== 'create' ? currentRecord?.placeCode : undefined,
          );
        }
      },
    });
    onClose();
  }, [tabId, moduleName, minimize, onClose, onRestoreModal]);

  if (minimizeRef) minimizeRef.current = handleMinimize;

  const handleSaveClick = useCallback(() => {
    const errors: Record<string, string> = {};
    const code = formData.placeCode.trim();
    const name = formData.placeName.trim();
    const pin = formData.pincode.trim();
    if (!code) errors.placeCode = 'Place code is required';
    else if (code.length > 30) errors.placeCode = 'Max 30 characters';
    else if (!/^[A-Z0-9-]+$/.test(code)) errors.placeCode = 'Uppercase letters, digits, hyphens only';
    if (!name) errors.placeName = 'Place name is required';
    else if (name.length > 100) errors.placeName = 'Max 100 characters';
    if (pin && !/^\d{6}$/.test(pin)) errors.pincode = 'Pincode must be 6 digits';
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      requestAnimationFrame(() => {
        const el = document.querySelector<HTMLElement>(`[data-field="${Object.keys(errors)[0]}"]`);
        if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); el.querySelector<HTMLInputElement>('input')?.focus(); }
      });
      return;
    }
    setFieldErrors({});
    setSaveConfirmOpen(true);
  }, [formData]);

  const handleConfirmSave = useCallback(async () => {
    setSaveConfirmOpen(false);
    setIsSaving(true);
    try {
      const code = formData.placeCode.trim().toUpperCase();
      const name = formData.placeName.trim();
      const stateProvinceId = formData.stateProvinceId || null;
      const pincode = formData.pincode.trim() || null;
      if (mode === 'create' || mode === 'copy') {
        await courtPlaceService.create({ placeCode: code, placeName: name, stateProvinceId, pincode, statusCode: formData.statusCode });
        notificationService.toast.success(`Court Place "${code}" created successfully.`);
      } else if (record) {
        await courtPlaceService.update(record.id, {
          placeCode: code, placeName: name, stateProvinceId, pincode,
          statusCode: formData.statusCode, rowVersion: record.rowVersion,
        });
        notificationService.toast.success(`Court Place "${code}" updated successfully.`);
      }
      setIsSaving(false);
      onSaved();
      onClose();
    } catch (err: any) {
      setIsSaving(false);
      const status = err?.response?.status;
      const serverMsg = err?.response?.data?.message;
      if (status === 409) {
        if (serverMsg?.toLowerCase().includes('modified')) {
          notificationService.toast.error('This court place was modified by another user. Refresh and try again.', 6000);
        } else {
          notificationService.toast.error(serverMsg || 'A court place with this code already exists.', 6000);
        }
      } else if (status === 400 && err?.response?.data?.errors?.length) {
        notificationService.toast.error((err.response.data.errors as string[]).join(' '), 6000);
      } else {
        notificationService.toast.error(serverMsg || 'Failed to save court place. Please try again.', 6000);
      }
    }
  }, [formData, mode, record, onSaved, onClose]);

  const handleDiscard = useCallback(() => { setDiscardConfirmOpen(false); onClose(); }, [onClose]);

  if (!isOpen) return null;

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-sm">
        {isSaving && (
          <div style={{
            position: 'absolute', inset: 0, background: 'var(--modal-loading-overlay)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            zIndex: 10, borderRadius: 'inherit', gap: '0.75rem',
          }}>
            <div style={{
              width: '2rem', height: '2rem',
              border: '3px solid var(--border-color)', borderTopColor: '#2563eb',
              borderRadius: '50%', animation: 'spin 0.8s linear infinite',
            }} />
            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 500 }}>Saving...</span>
          </div>
        )}

        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">{mode === 'view' ? 'View Court Place' : mode === 'copy' ? 'Copy Court Place' : mode === 'create' ? 'Add Court Place' : 'Edit Court Place'}</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            {tabId && (
              <button className="modal-header-btn" onClick={handleMinimize} title="Minimize to bar" disabled={isSaving}>
                <IconSystem.chevronDown size={16} />
              </button>
            )}
            <button className="modal-close" onClick={handleClose} disabled={isSaving}>
              <IconSystem.x size={20} />
            </button>
          </div>
        </div>

        <div className="modal-content">
          <fieldset disabled={mode === 'view'} style={{ border: 'none', padding: 0, margin: 0, minWidth: 0 }}>
          <div className="form-group" data-field="placeCode">
            <label className="form-label">Place Code <span className="text-danger">*</span></label>
            <input
              type="text"
              className={`form-input${getFieldError('placeCode') ? ' form-field-error' : ''}`}
              value={formData.placeCode}
              maxLength={30}
              placeholder="MUM-FORT, TRR-THRS..."
              onChange={(e) => { clearFieldError('placeCode'); setFormData(p => ({ ...p, placeCode: e.target.value.toUpperCase() })); }}
              disabled={isSaving}
            />
            {getFieldError('placeCode') && <div className="form-error-message">{getFieldError('placeCode')}</div>}
          </div>

          <div className="form-group" data-field="placeName">
            <label className="form-label">Place Name <span className="text-danger">*</span></label>
            <input
              type="text"
              className={`form-input${getFieldError('placeName') ? ' form-field-error' : ''}`}
              value={formData.placeName}
              maxLength={100}
              placeholder="Thrissur"
              onChange={(e) => { clearFieldError('placeName'); setFormData(p => ({ ...p, placeName: e.target.value })); }}
              disabled={isSaving}
            />
            {getFieldError('placeName') && <div className="form-error-message">{getFieldError('placeName')}</div>}
          </div>

          <div className="form-group" data-field="stateProvinceId">
            <SyutSelect
              label="State"
              options={combinedStateOptions}
              value={formData.stateProvinceId}
              onChange={(v) => { clearFieldError('stateProvinceId'); setFormData(p => ({ ...p, stateProvinceId: (v as string) || '' })); }}
              error={getFieldError('stateProvinceId')}
              isClearable={false}
              placeholder="— No state —"
              isDisabled={mode === 'view' || (isSaving)}
            />
          </div>

          <div className="form-group" data-field="pincode">
            <label className="form-label">Pincode</label>
            <input
              type="text"
              className={`form-input${getFieldError('pincode') ? ' form-field-error' : ''}`}
              value={formData.pincode}
              maxLength={10}
              placeholder="680001"
              onChange={(e) => { clearFieldError('pincode'); setFormData(p => ({ ...p, pincode: e.target.value })); }}
              disabled={isSaving}
            />
            {getFieldError('pincode') && <div className="form-error-message">{getFieldError('pincode')}</div>}
          </div>

          <div className="form-group" data-field="statusCode">
            <SyutSelect
              label="Status"
              required
              options={STATUS_OPTIONS}
              value={formData.statusCode}
              onChange={(v) => setFormData(p => ({ ...p, statusCode: ((v as string) || 'ACTIVE') as PlaceStatus }))}
              isClearable={false}
              isDisabled={mode === 'view' || (isSaving)}
            />
          </div>
        </fieldset>
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={handleClose} disabled={isSaving}>{mode === 'view' ? 'Close' : 'Cancel'}</button>
          {mode !== 'view' && (<button className="btn btn-primary" onClick={handleSaveClick} disabled={isSaving}>
            {mode === 'copy' ? 'Save' : mode === 'create' ? 'Save' : 'Update'}
          </button>)}
        </div>
      </div>

      <ConfirmDialog
        open={saveConfirmOpen}
        title={mode === 'copy' ? 'Confirm Create' : mode === 'create' ? 'Confirm Create' : 'Confirm Update'}
        message={(mode === 'create' || mode === 'copy')
          ? `Create court place "${formData.placeCode.trim().toUpperCase()}"?`
          : `Update court place "${formData.placeCode.trim().toUpperCase()}"?`}
        confirmLabel="Confirm"
        cancelLabel="Cancel"
        variant="default"
        onConfirm={handleConfirmSave}
        onCancel={() => setSaveConfirmOpen(false)}
      />

      <ConfirmDialog
        open={discardConfirmOpen}
        title="Discard Changes?"
        message="You have unsaved changes. Do you want to discard them?"
        confirmLabel="Discard"
        cancelLabel="Keep Editing"
        variant="danger"
        onConfirm={handleDiscard}
        onCancel={() => setDiscardConfirmOpen(false)}
      />
    </>
  );
};

export default CourtPlaceFormModal;
